import {TextHorizontalAlignType, TextStyles} from '@PosterWhiteboard/classes/text-styles.class';
import {hexToRgba} from '@Utils/color.util';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {createSolidFill, createTransparentFill} from '@PosterWhiteboard/classes/fill.types';
import {createGradientFillForSurface, FillSurface} from '@PosterWhiteboard/classes/fill-surface';
import type {SubtitleStyles} from '@PosterWhiteboard/items/transcript-item/subtitle/subtitle.types';
import type {SubtitleTemplate, SubtitleTemplateProperties} from '@PosterWhiteboard/items/transcript-item/subtitle/template-styles.types';
import {SubtitleTemplateType} from '@PosterWhiteboard/items/transcript-item/subtitle/template-styles.types';
import type {DeepPartial} from '@/global';
import {getDefaultShadowObjectFromShadowType} from '@Utils/fabric-shadow.util';
import {ShadowType} from '@PosterWhiteboard/poster/poster-backend.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';

export const getDefaultTranscriptLayoutProperties = (): DeepPartial<SubtitleStyles> => {
  const defaultTextStyles = new TextStyles();
  const defaultShadowOptionsObject = getDefaultShadowObjectFromShadowType(ShadowType.NONE, ITEM_TYPE.TRANSCRIPT);

  const {fontSize, ...otherDefaultTextStyles} = defaultTextStyles.toObject();

  return {
    backgroundBorderRadius: 0,
    sentenceTextStyles: {
      ...otherDefaultTextStyles,
      textAlign: TextHorizontalAlignType.CENTER,
      stroke: false,
      fontWeight: 'normal',
      fontFamily: 'RalewayRegular',
      fill: createTransparentFill(),
    },
    activeWordTextStyles: {
      ...otherDefaultTextStyles,
      textAlign: TextHorizontalAlignType.CENTER,
      stroke: false,
      fontWeight: 'normal',
      fontFamily: 'RalewayRegular',
      fill: createTransparentFill(),
    },
    backgroundFill: createTransparentFill(),
    shadow: defaultShadowOptionsObject,
  };
};

export const getAllAvailableTemplateStylesAndProperties = (): Record<string, SubtitleTemplate> => {
  return {
    '1': {
      templateProperties: {
        id: '1',
        type: SubtitleTemplateType.SIMPLE,
        sortOrder: 1,
      },
      templateStyles: {
        idNormalTemplate: '1',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            fontWeight: 'bold',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontWeight: 'bold',
          },
        },
      },
    },
    '2': {
      templateProperties: {
        id: '2',
        type: SubtitleTemplateType.SIMPLE,
        sortOrder: 3,
      },
      templateStyles: {
        idNormalTemplate: '2',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 0.75,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontWeight: 'bold',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            stroke: true,
            strokeColor: [255, 255, 255, 1],
            strokeWidth: 0.75,
            fill: createSolidFill(hexToRgba('000000')),
            fontWeight: 'bold',
          },
        },
      },
    },
    '3': {
      templateProperties: {
        id: '3',
        type: SubtitleTemplateType.SIMPLE,
        sortOrder: 6,
      },
      templateStyles: {
        idNormalTemplate: '3',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 0.6,
            fill: createSolidFill([250, 221, 85, 1]),
            fontWeight: 'bold',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            stroke: true,
            strokeColor: [255, 255, 255, 1],
            strokeWidth: 0.6,
            fill: createSolidFill([5, 34, 170, 1]),
            fontWeight: 'bold',
          },
        },
      },
    },
    '4': {
      templateProperties: {
        id: '4',
        type: SubtitleTemplateType.SIMPLE,
        sortOrder: 5,
      },
      templateStyles: {
        idNormalTemplate: '4',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontWeight: 'bold',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill([250, 221, 85, 1]),
            fontWeight: 'bold',
          },
          backgroundFill: createSolidFill([0, 0, 0, 0.57]),
          backgroundBorderRadius: 30,
        },
      },
    },
    '5': {
      templateProperties: {
        id: '5',
        type: SubtitleTemplateType.HIGHLIGHT_WORD,
        sortOrder: 14,
      },
      templateStyles: {
        idHighlighCurrentWordTemplate: '1',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('CACACA')),
            fontFamily: 'FjallaOneRegular',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'FjallaOneRegular',
            fontWeight: 'bold',
            stroke: true,
            strokeWidth: 0.01,
            strokeColor: hexToRgba('CACACA'),
          },
        },
      },
    },
    '6': {
      templateProperties: {
        id: '6',
        type: SubtitleTemplateType.HIGHLIGHT_WORD,
        sortOrder: 20,
      },
      templateStyles: {
        idHighlighCurrentWordTemplate: '2',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('CACACA')),
            fontFamily: 'FjallaOneRegular',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FFFF00')),
            fontFamily: 'FjallaOneRegular',
            fontWeight: 'bold',
            stroke: true,
            strokeWidth: 0.01,
            strokeColor: hexToRgba('CACACA'),
          },
        },
      },
    },
    '7': {
      templateProperties: {
        id: '7',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 4,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '1',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'UbuntuMono',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'UbuntuMono',
          },
        },
      },
    },
    '8': {
      templateProperties: {
        id: '8',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 21,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '2',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFF00')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'UbuntuMono',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('0000FF')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'UbuntuMono',
          },
        },
      },
    },
    '9': {
      templateProperties: {
        id: '9',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 16,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '3',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'BebasNeue',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'BebasNeue',
          },
        },
      },
    },
    '10': {
      templateProperties: {
        id: '10',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 2,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '4',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFF00')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'BebasNeue',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('0000FF')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'BebasNeue',
          },
        },
      },
    },
    '11': {
      templateProperties: {
        id: '11',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 15,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '5',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFF00')),
            stroke: true,
            strokeColor: [135, 63, 0, 1],
            strokeWidth: 1,
            fontFamily: 'HoltwoodOneSC',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('0000FF')),
            stroke: true,
            strokeColor: [135, 63, 0, 1],
            strokeWidth: 1,
            fontFamily: 'HoltwoodOneSC',
          },
          shadow: {
            ...getDefaultShadowObjectFromShadowType(ShadowType.CUSTOM, ITEM_TYPE.TRANSCRIPT),
            angle: 70,
            distance: 2,
            blur: 0,
            color: [255, 255, 255, 1],
          },
        },
      },
    },
    '12': {
      templateProperties: {
        id: '12',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 7,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '6',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'Bangers',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'Bangers',
          },
          shadow: {
            ...getDefaultShadowObjectFromShadowType(ShadowType.CUSTOM, ITEM_TYPE.TRANSCRIPT),
            angle: 0,
            distance: 0,
            blur: 10,
            color: [255, 255, 255, 1],
          },
        },
      },
    },
    '13': {
      templateProperties: {
        id: '13',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 26,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '7',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFF00')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'Bangers',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('0000FF')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'Bangers',
          },
          shadow: {
            ...getDefaultShadowObjectFromShadowType(ShadowType.CUSTOM, ITEM_TYPE.TRANSCRIPT),
            angle: 0,
            distance: 0,
            blur: 10,
            color: [255, 255, 255, 1],
          },
        },
      },
    },
    '14': {
      templateProperties: {
        id: '14',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 32,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '8',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'RobotoBold',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'RobotoBold',
          },
          shadow: {
            ...getDefaultShadowObjectFromShadowType(ShadowType.CUSTOM, ITEM_TYPE.TRANSCRIPT),
            angle: 45,
            distance: 4,
            blur: 3,
            color: [0, 0, 0, 1],
          },
        },
      },
    },
    '15': {
      templateProperties: {
        id: '15',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 31,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '9',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createGradientFillForSurface(FillTypes.LINEAR_GRADIENT, [hexToRgba('FFFFFF'), hexToRgba('FFAF00')], FillSurface.TEXT),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'RobotoBold',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createGradientFillForSurface(FillTypes.LINEAR_GRADIENT, [hexToRgba('FFFFFF'), hexToRgba('0050FF')], FillSurface.TEXT),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 1,
            fontFamily: 'RobotoBold',
          },
          shadow: {
            ...getDefaultShadowObjectFromShadowType(ShadowType.CUSTOM, ITEM_TYPE.TRANSCRIPT),
            angle: 45,
            distance: 4,
            blur: 3,
            color: [0, 0, 0, 1],
          },
        },
      },
    },
    '16': {
      templateProperties: {
        id: '16',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 23,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '10',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          backgroundFill: createSolidFill([0, 0, 0, 0.57]),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFF00')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 0.5,
            fontFamily: 'ArchivoBlackRegular',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('0000FF')),
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 0.5,
            fontFamily: 'ArchivoBlackRegular',
          },
          backgroundBorderRadius: 15,
        },
      },
    },
    '17': {
      templateProperties: {
        id: '17',
        type: SubtitleTemplateType.PROGRESS,
        sortOrder: 17,
      },
      templateStyles: {
        idProgressTemplateStyle: '1',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'BebasNeue',
            stroke: true,
            strokeColor: [255, 255, 255, 1],
            strokeWidth: 0.1,
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('00000')),
            fontFamily: 'BebasNeue',
            stroke: true,
            strokeColor: [255, 255, 255, 1],
            strokeWidth: 0.1,
            fontWeight: 'bold',
          },
          backgroundFill: createSolidFill([250, 221, 85, 0.8]),
          backgroundBorderRadius: 15,
        },
      },
    },
    '18': {
      templateProperties: {
        id: '18',
        type: SubtitleTemplateType.HIGHLIGHT_WORD,
        sortOrder: 9,
      },
      templateStyles: {
        idHighlighCurrentWordTemplate: '3',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'BebasNeue',
            stroke: true,
            strokeColor: [255, 255, 255, 1],
            strokeWidth: 0.1,
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('00000')),
            fontFamily: 'BebasNeue',
            fontWeight: 'bold',
            stroke: true,
            strokeColor: [255, 255, 255, 1],
            strokeWidth: 0.1,
          },
          backgroundFill: createSolidFill(hexToRgba('FF9400', 0.8)),
          backgroundBorderRadius: 15,
        },
      },
    },
    '19': {
      templateProperties: {
        id: '19',
        type: SubtitleTemplateType.SIMPLE,
        sortOrder: 37,
      },
      templateStyles: {
        idNormalTemplate: '5',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'BebasNeue',
            fontWeight: 'bold',
            stroke: true,
            strokeColor: [0, 0, 0, 1],
            strokeWidth: 0.2,
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            fontFamily: 'BebasNeue',
            fontWeight: 'bold',
            stroke: true,
            strokeColor: [255, 255, 255, 1],
            strokeWidth: 0.2,
          },
          backgroundFill: createSolidFill(hexToRgba('FF9400', 0.8)),
          backgroundBorderRadius: 15,
        },
      },
    },
    '20': {
      templateProperties: {
        id: '20',
        type: SubtitleTemplateType.SIMPLE,
        sortOrder: 10,
      },
      templateStyles: {
        idNormalTemplate: '6',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            fontFamily: 'LeagueSpartanBold',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FAD956')),
            fontFamily: 'LeagueSpartanBold',
          },
          backgroundFill: createSolidFill(hexToRgba('FFFFFF', 1)),
          backgroundBorderRadius: 0,
        },
      },
    },
    '21': {
      templateProperties: {
        id: '21',
        type: SubtitleTemplateType.HIGHLIGHT_WORD,
        sortOrder: 35,
      },
      templateStyles: {
        idHighlighCurrentWordTemplate: '4',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'QuestrialRegular',
            stroke: true,
            strokeColor: hexToRgba('000000'),
            strokeWidth: 0.75,
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FF0000')),
            fontFamily: 'QuestrialRegular',
            fontWeight: 'bold',
            stroke: true,
            strokeColor: hexToRgba('FFFFFF'),
            strokeWidth: 0.75,
          },
        },
      },
    },
    '22': {
      templateProperties: {
        id: '22',
        type: SubtitleTemplateType.SIMPLE,
        sortOrder: 8,
      },
      templateStyles: {
        idNormalTemplate: '7',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFEBCF')),
            fontFamily: 'IntroRegular',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('E98E0F')),
            fontFamily: 'IntroRegular',
          },
          backgroundFill: createSolidFill(hexToRgba('025B29', 1)),
          backgroundBorderRadius: 28,
        },
      },
    },
    '23': {
      templateProperties: {
        id: '23',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 41,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '11',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            fontFamily: 'LeagueSpartanBold',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'LeagueSpartanBold',
          },
          backgroundFill: createSolidFill(hexToRgba('FAD956', 1)),
          backgroundBorderRadius: 0,
        },
      },
    },
    '24': {
      templateProperties: {
        id: '24',
        type: SubtitleTemplateType.PROGRESS,
        sortOrder: 18,
      },
      templateStyles: {
        idProgressTemplateStyle: '2',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'LeagueSpartanBold',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FAD956')),
            fontFamily: 'LeagueSpartanBold',
          },
          backgroundFill: createSolidFill(hexToRgba('A53AE2', 1)),
          backgroundBorderRadius: 0,
        },
      },
    },
    '25': {
      templateProperties: {
        id: '25',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 40,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '12',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'FuturaLTBook',
            stroke: true,
            strokeWidth: 1,
            strokeColor: hexToRgba('124E2C'),
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FAD956')),
            fontFamily: 'FuturaLTBook',
            stroke: true,
            strokeWidth: 1,
            strokeColor: hexToRgba('124E2C'),
          },
        },
      },
    },
    '26': {
      templateProperties: {
        id: '26',
        type: SubtitleTemplateType.SIMPLE,
        sortOrder: 22,
      },
      templateStyles: {
        idNormalTemplate: '8',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            fontFamily: 'SFSpeedwaystar',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FAD956')),
            fontFamily: 'SFSpeedwaystar',
          },
          shadow: {
            ...getDefaultShadowObjectFromShadowType(ShadowType.CUSTOM, ITEM_TYPE.TRANSCRIPT),
            angle: 36,
            distance: 5,
            blur: 0,
            color: hexToRgba('32ED34', 1),
          },
        },
      },
    },
    '27': {
      templateProperties: {
        id: '27',
        type: SubtitleTemplateType.PROGRESS,
        sortOrder: 29,
      },
      templateStyles: {
        idProgressTemplateStyle: '3',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'RasaRegular',
            stroke: true,
            strokeWidth: 1,
            strokeColor: hexToRgba('000000'),
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FAD956')),
            fontFamily: 'RasaRegular',
            stroke: true,
            strokeWidth: 1,
            strokeColor: hexToRgba('000000'),
          },
          shadow: {
            ...getDefaultShadowObjectFromShadowType(ShadowType.CUSTOM, ITEM_TYPE.TRANSCRIPT),
            angle: 35,
            distance: 6,
            blur: 4,
            color: hexToRgba('000000', 1),
          },
        },
      },
    },
    '28': {
      templateProperties: {
        id: '28',
        type: SubtitleTemplateType.SIMPLE,
        sortOrder: 11,
      },
      templateStyles: {
        idNormalTemplate: '9',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            fontFamily: 'OutfitRegular',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'OutfitRegular',
          },
          backgroundFill: createSolidFill(hexToRgba('2ECEFF', 1)),
          backgroundBorderRadius: 12,
        },
      },
    },
    '29': {
      templateProperties: {
        id: '29',
        type: SubtitleTemplateType.SIMPLE,
        sortOrder: 30,
      },
      templateStyles: {
        idNormalTemplate: '10',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            fontFamily: 'CicleFina',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FAD956')),
            fontFamily: 'CicleFina',
          },
        },
      },
    },
    '30': {
      templateProperties: {
        id: '30',
        type: SubtitleTemplateType.SIMPLE,
        sortOrder: 19,
      },
      templateStyles: {
        idNormalTemplate: '11',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FDB953')),
            fontFamily: 'LeagueSpartanBold',
            stroke: true,
            strokeColor: hexToRgba('000000'),
            strokeWidth: 0.56,
            letterSpacing: 1,
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FF9900')),
            fontFamily: 'LeagueSpartanBold',
            stroke: true,
            strokeColor: hexToRgba('000000'),
            strokeWidth: 0.56,
            letterSpacing: 1,
          },
        },
      },
    },
    '31': {
      templateProperties: {
        id: '31',
        type: SubtitleTemplateType.SIMPLE,
        sortOrder: 24,
      },
      templateStyles: {
        idNormalTemplate: '12',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'BebasNeueRegular',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FDB953')),
            fontFamily: 'BebasNeueRegular',
          },
          backgroundFill: createSolidFill(hexToRgba('1B5D6A', 0.57)),
          backgroundBorderRadius: 30,
        },
      },
    },
    '32': {
      templateProperties: {
        id: '32',
        type: SubtitleTemplateType.HIGHLIGHT_WORD,
        sortOrder: 13,
      },
      templateStyles: {
        idHighlighCurrentWordTemplate: '5',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('CACACA')),
            fontFamily: 'FuturaLTRegular',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('B24DCE')),
            fontFamily: 'FuturaLTRegular',
          },
        },
      },
    },
    '33': {
      templateProperties: {
        id: '33',
        type: SubtitleTemplateType.SIMPLE,
        sortOrder: 25,
      },
      templateStyles: {
        idNormalTemplate: '13',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            fontFamily: 'SFSpeedwaystar',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('B24DCE')),
            fontFamily: 'SFSpeedwaystar',
          },
          shadow: {
            ...getDefaultShadowObjectFromShadowType(ShadowType.CUSTOM, ITEM_TYPE.TRANSCRIPT),
            angle: 36,
            distance: 5,
            blur: 8,
            color: hexToRgba('FFC882', 1),
          },
        },
      },
    },
    '34': {
      templateProperties: {
        id: '34',
        type: SubtitleTemplateType.HIGHLIGHT_WORD,
        sortOrder: 27,
      },
      templateStyles: {
        idHighlighCurrentWordTemplate: '6',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'LiberationSerif',
            stroke: true,
            strokeColor: hexToRgba('000000'),
            strokeWidth: 0.51,
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('4FE4EA')),
            fontFamily: 'LiberationSerif',
            stroke: true,
            strokeColor: hexToRgba('000000'),
            strokeWidth: 0.51,
          },
        },
      },
    },
    '35': {
      templateProperties: {
        id: '35',
        type: SubtitleTemplateType.HIGHLIGHT_WORD,
        sortOrder: 38,
      },
      templateStyles: {
        idHighlighCurrentWordTemplate: '7',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'SourceSansProBlackRegular',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            fontFamily: 'SourceSansProBlackRegular',
          },
          backgroundFill: createSolidFill(hexToRgba('FFDA2E', 0.8)),
          backgroundBorderRadius: 15,
        },
      },
    },
    '36': {
      templateProperties: {
        id: '36',
        type: SubtitleTemplateType.HIGHLIGHT_WORD,
        sortOrder: 33,
      },
      templateStyles: {
        idHighlighCurrentWordTemplate: '8',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('CACACA')),
            fontFamily: 'Sveningsson',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'Sveningsson',
            stroke: true,
            strokeWidth: 0.15,
            strokeColor: hexToRgba('CACACA'),
          },
        },
      },
    },
    '37': {
      templateProperties: {
        id: '37',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 12,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '13',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('B24DCE')),
            fontFamily: 'LeagueSpartanBold',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            fontFamily: 'LeagueSpartanBold',
          },
          backgroundFill: createSolidFill(hexToRgba('FAD956', 1)),
          backgroundBorderRadius: 0,
        },
      },
    },
    '38': {
      templateProperties: {
        id: '38',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 39,
        isHidden: true,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '14',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'FuturaLTBook',
            stroke: true,
            strokeColor: hexToRgba('124E2C'),
            strokeWidth: 1,
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FAD956')),
            fontFamily: 'FuturaLTBook',
            stroke: true,
            strokeColor: hexToRgba('124E2C'),
            strokeWidth: 1,
          },
        },
      },
    },
    '39': {
      templateProperties: {
        id: '39',
        type: SubtitleTemplateType.PROGRESS,
        sortOrder: 42,
      },
      templateStyles: {
        idProgressTemplateStyle: '4',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'Homestead',
            stroke: true,
            strokeColor: hexToRgba('000000'),
            strokeWidth: 1,
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FAD956')),
            fontFamily: 'Homestead',
            stroke: true,
            strokeColor: hexToRgba('000000'),
            strokeWidth: 1,
          },
          shadow: {
            ...getDefaultShadowObjectFromShadowType(ShadowType.CUSTOM, ITEM_TYPE.TRANSCRIPT),
            angle: 35,
            distance: 6,
            blur: 4,
            color: hexToRgba('000000', 0.4),
          },
        },
      },
    },
    '40': {
      templateProperties: {
        id: '40',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 34,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '15',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FAD956')),
            fontFamily: 'chicleregular',
            stroke: true,
            strokeColor: hexToRgba('38500D'),
            strokeWidth: 0.76,
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('83CC45')),
            fontFamily: 'chicleregular',
            stroke: true,
            strokeColor: hexToRgba('38500D'),
            strokeWidth: 0.76,
          },
        },
      },
    },
    '41': {
      templateProperties: {
        id: '41',
        type: SubtitleTemplateType.ONE_WORD,
        sortOrder: 28,
      },
      templateStyles: {
        idCurrentWordOnlyTemplate: '16',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            fontFamily: 'chicleregular',
            stroke: true,
            strokeColor: hexToRgba('CC6400'),
            strokeWidth: 1,
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'chicleregular',
            stroke: true,
            strokeColor: hexToRgba('CC6400'),
            strokeWidth: 1,
          },
        },
      },
    },
    '42': {
      templateProperties: {
        id: '42',
        type: SubtitleTemplateType.SIMPLE,
        sortOrder: 36,
      },
      templateStyles: {
        idNormalTemplate: '14',
        stylesToApply: {
          ...getDefaultTranscriptLayoutProperties(),
          sentenceTextStyles: {
            ...getDefaultTranscriptLayoutProperties().sentenceTextStyles,
            fill: createSolidFill(hexToRgba('FFFFFF')),
            fontFamily: 'PressStart2PRegular',
          },
          activeWordTextStyles: {
            ...getDefaultTranscriptLayoutProperties().activeWordTextStyles,
            fill: createSolidFill(hexToRgba('000000')),
            fontFamily: 'PressStart2PRegular',
          },
          backgroundFill: createSolidFill(hexToRgba('BF1F4A', 1)),
          backgroundBorderRadius: 12,
        },
      },
    },
  };
};

export const getDefaultSelectedSubtitleTemplateProperties = (): SubtitleTemplateProperties => {
  return getAllAvailableTemplateStylesAndProperties()['1'].templateProperties;
};

export const getTemplateStylesAndProperties = (hideAnimationTemplates = false): Record<string, SubtitleTemplate> => {
  if (hideAnimationTemplates) {
    return getAllTemplateStylesAndPropertiesWithoutAnimations();
  }

  return getAllAvailableTemplateStylesAndProperties();
};

const getAllTemplateStylesAndPropertiesWithoutAnimations = (): Record<string, SubtitleTemplate> => {
  const templateStylesWithoutAnimations: Record<string, SubtitleTemplate> = {};

  Object.entries(getAllAvailableTemplateStylesAndProperties()).forEach(([key, templateStyleObj]) => {
    if (templateStyleObj.templateProperties.type === SubtitleTemplateType.SIMPLE || templateStyleObj.templateProperties.type === SubtitleTemplateType.ONE_WORD) {
      templateStylesWithoutAnimations[key] = templateStyleObj;
    }
  });

  return templateStylesWithoutAnimations;
};
